/**
 * components/landing/home/Pricing.jsx — Section 12.
 *
 * Three-card pricing at USD ($) tiers — driven by the CENTRALIZED
 * PRICING ENGINE (`/app/lib/pricing.js`):
 *   Quarterly  $25,000       (3 months)
 *   Half-year  $40,000       (6 months, Most Popular)
 *   Annual     $60,000       (12 months, Best Value)
 * Includes Stripe reassurance strip below the cards.
 *
 * 06/2026 — CTA buttons now open the shared <WlCheckoutModal/> so
 * homepage visitors get the same inline discount-code + Stripe
 * Checkout flow as the /pricing "Commercial Decision Center".
 */

import { useState } from 'react'
import clsx from 'clsx'
import { Container, Eyebrow, SectionHeading } from '../design-system'
import { Check, ShieldCheck, ArrowRight, Sparkles } from 'lucide-react'
import { WL_PLAN_BY_ID, WL_BENEFITS } from '@/lib/pricing'
import WlCheckoutModal from '../pricing/WlCheckoutModal'

const PLANS = [
  {
    id: 'quarterly',
    planId: WL_PLAN_BY_ID.quarterly.id,
    label: 'Quarterly',
    price: WL_PLAN_BY_ID.quarterly.price_usd,
    amount_cents: WL_PLAN_BY_ID.quarterly.amount_cents,
    per: '/ 3 mo',
    duration: '3 Months License',
    tag: 'Great for pilots & early-stage operators',
  },
  {
    id: 'half',
    planId: WL_PLAN_BY_ID.halfyearly.id,
    label: 'Half-Year',
    price: WL_PLAN_BY_ID.halfyearly.price_usd,
    amount_cents: WL_PLAN_BY_ID.halfyearly.amount_cents,
    per: '/ 6 mo',
    duration: '6 Months License',
    tag: 'Most popular for scaling desks',
    highlighted: true,
    badge: 'Most Popular',
    save: 'Save $10,000 vs quarterly',
  },
  {
    id: 'annual',
    planId: WL_PLAN_BY_ID.annual.id,
    label: 'Annual',
    price: WL_PLAN_BY_ID.annual.price_usd,
    amount_cents: WL_PLAN_BY_ID.annual.amount_cents,
    per: '/ 12 mo',
    duration: '12 Months License',
    tag: 'Best value for committed operators',
    badge: 'Best Value',
    save: 'Save $40,000 vs quarterly',
  },
]

const INCLUDED = [
  'White-Label License',
  'Full Trading Platform',
  'Platform Updates',
  'Stripe Integration',
  'Priority Support',
  'Multi-Region Cloud',
  'Enterprise RBAC',
]

const fmt = (n) => n.toLocaleString('en-US')

const Pricing = () => {
  const [checkoutPlan, setCheckoutPlan] = useState(null)

  const openCheckout = (p) => {
    setCheckoutPlan({
      planId:       p.planId,
      label:        `${p.label} License`,
      duration:     p.duration,
      amount_cents: p.amount_cents,
    })
  }

  return (
    <section id="pricing" className="relative py-24 md:py-40 scroll-mt-32">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-50"
        style={{ background: 'radial-gradient(50% 40% at 50% 0%, rgba(255,193,7,0.14), transparent 70%)' }} />
      <Container>
        <SectionHeading
          eyebrow="Pricing"
          title={<>One licence. <span className="text-brand-gold">Every capability</span>.</>}
          subtitle="Every plan includes the complete Kalki Ai Cloud stack — White-Label, AI, Trading, Academy, Billing, Analytics. No feature paywalls."
          align="center"
          className="items-center mx-auto"
        />

        <div className="mt-14 grid gap-4 md:grid-cols-3 max-w-5xl mx-auto">
          {PLANS.map((p) => (
            <div
              key={p.id}
              className={clsx(
                'relative rounded-3xl border p-6 md:p-8 flex flex-col gap-4 transition-all duration-300',
                p.highlighted
                  ? 'border-brand-gold/50 bg-gradient-to-b from-brand-gold/[0.10] to-transparent shadow-[0_30px_80px_-30px_rgba(255,193,7,0.4)] -translate-y-2 md:-translate-y-4'
                  : 'border-white/10 bg-white/[0.02] hover:border-brand-gold/25',
              )}
            >
              {p.badge && (
                <div className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full border border-brand-gold/40 bg-black px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-brand-gold">
                  <Sparkles className="h-3 w-3" /> {p.badge}
                </div>
              )}
              <div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{p.label}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-4xl md:text-5xl font-semibold text-white tracking-tight">${fmt(p.price)}</span>
                  <span className="text-sm text-zinc-500">{p.per}</span>
                </div>
                <div className="mt-1 text-[12px] text-zinc-400">{p.duration}</div>
                {p.save && <div className="mt-1 text-[11px] text-brand-emerald font-mono">{p.save}</div>}
                <div className="mt-3 text-[12px] text-zinc-500">{p.tag}</div>
              </div>
              <div className="h-px bg-white/5" />
              <ul className="space-y-2 flex-1">
                {INCLUDED.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-[13px] text-zinc-200">
                    <Check className={clsx('h-4 w-4', p.highlighted ? 'text-brand-gold' : 'text-brand-emerald')} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                data-testid={`checkout-button-${p.planId}`}
                onClick={() => openCheckout(p)}
                className={clsx(
                  'inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm py-3 transition-all mt-2 cursor-pointer',
                  p.highlighted
                    ? 'bg-gradient-to-r from-brand-gold to-[#FFE17A] text-black shadow-[0_10px_40px_-10px_rgba(255,193,7,0.55)] hover:shadow-[0_14px_46px_-8px_rgba(255,193,7,0.75)]'
                    : 'border border-white/10 bg-white/[0.03] text-white hover:bg-white/[0.06]',
                )}
              >
                Launch Now
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        {/* Stripe reassurance */}
        <div className="mt-8 flex flex-col md:flex-row items-center justify-center gap-3 text-[11px] text-zinc-500">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-brand-emerald" />
            Secure payments powered by <span className="text-white font-semibold">Stripe</span>
          </span>
          <span className="hidden md:inline">·</span>
          <span>Cancel anytime.  Prices exclusive of applicable taxes.</span>
        </div>
      </Container>

      <WlCheckoutModal
        open={!!checkoutPlan}
        plan={checkoutPlan}
        onClose={() => setCheckoutPlan(null)}
      />
    </section>
  )
}

export default Pricing
